import { track } from "./track";
import { componentId } from "./componentId";

const NAME_ATTRIBUTES = ["data-track", "data-automation"];
const NAMED_SELECTOR = NAME_ATTRIBUTES.map((attribute) => `[${attribute}]`).join(",");
const CLICKABLE_SELECTOR = "button, a, [role='button'], .btn, input[type='button'], input[type='submit']";
const PANEL_SELECTOR = "[data-track-panel], [data-x-origin], .right-modal, .modal, [role='dialog'], .modelSettingsModal";
const MAX_TEXT = 40;
const REPEAT_MS = 700;
const IGNORE_SELECTOR = "[data-track-ignore], .pagination, .-pagination, .pagination-bottom";
const BUTTON_NAME_LEVELS = 2;
const SELECT_NAME_LEVELS = 6;
const CHART_NAME_LEVELS = 4;
const HEADING_LEVELS = 5;
const HEADING_SELECTOR = ".card-title, h1, h2, h3, h4, h5";
const LABEL_SELECTOR = ".buttonLabel, .controlsContainerTitle, label";
const LABEL_LEVELS = 3;
const LABEL_MAX_BUTTONS = 2;
const CHART_SELECTOR = ".highcharts-container, .js-plotly-plot, [data-track-chart]";
const CHART_TITLE_SELECTOR = ".highcharts-title, .gtitle";
const INSPECT_AFTER_MS = 2000;
const INSPECT_AGAIN_MS = 60000;
const HOVER_CHECK_MS = 250;
const PAGE_TAB_SELECTOR = ".nav-tabs .nav-link, .nav-pills .nav-link, [role='tab']";

const SELECT_OPTION = "[class*='__option']";
const SELECT_CLEAR = "[class*='__clear-indicator']";
const SELECT_REMOVE = "[class*='__multi-value__remove']";
const SELECT_CONTROL = "[class*='__control']";
const SELECT_CONTAINER = "[class*='-container']";

let lastClick = null;
let lastSelect = null;

export const getLastClick = () => lastClick;

const clean = (text) =>
  String(text || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_TEXT);

const readName = (element) => {
  for (let index = 0; index < NAME_ATTRIBUTES.length; index += 1) {
    const value = element.getAttribute(NAME_ATTRIBUTES[index]);
    if (value && value.trim()) return value.trim();
  }
  return "";
};

const nearestNamed = (element, maxLevels) => {
  let current = element;
  for (let level = 0; current && level <= maxLevels; level += 1) {
    if (current.matches && current.matches(NAMED_SELECTOR)) return current;
    current = current.parentElement;
  }
  return null;
};

const ownsName = (named, element) => {
  if (!named || named === element) return true;
  return named.querySelectorAll(CLICKABLE_SELECTOR).length <= 1;
};

const readIcon = (element) => {
  const icon = element.matches("i, svg") ? element : element.querySelector("i, svg");
  if (!icon) return "";
  const names = String(icon.getAttribute("class") || "")
    .split(/\s+/)
    .filter((name) => /icon-|fa-/.test(name) && !/hover|white|primary|lg$/.test(name));
  return names.length ? `icon ${names[names.length - 1]}` : "";
};

const readLabelNear = (element) => {
  let current = element.parentElement;
  for (let level = 0; current && level < LABEL_LEVELS; level += 1) {
    if (current.querySelectorAll(CLICKABLE_SELECTOR).length > LABEL_MAX_BUTTONS) return "";
    const label = current.querySelector(LABEL_SELECTOR);
    if (label && clean(label.textContent)) return clean(label.textContent);
    current = current.parentElement;
  }
  return "";
};

const readLabel = (element) =>
  clean(element.getAttribute("aria-label") || element.getAttribute("title") || element.textContent || element.value) ||
  readLabelNear(element) ||
  readIcon(element);

let lastEmitted = { key: "", at: 0 };

const emit = (type, name, action, value, extra) => {
  const key = [type, name, action, value, extra && extra.text, extra && extra.component_id].join("|");
  const at = Date.now();
  const repeated = lastEmitted.key === key && at - lastEmitted.at < REPEAT_MS;
  lastEmitted = { key, at };
  if (!repeated) track(type, name, action, value, extra);
};

export const readPanel = (element) => {
  const panel = element.closest(PANEL_SELECTOR);
  if (!panel) return undefined;
  const name = panel.getAttribute("data-track-panel") || panel.getAttribute("data-x-origin");
  if (name) return name;
  const marker = panel.querySelector("[data-track-panel], [data-x-origin]");
  if (marker) return marker.getAttribute("data-track-panel") || marker.getAttribute("data-x-origin");
  const title = panel.querySelector(".right-modal__title, .modal-title");
  return title ? clean(title.textContent) : "unnamed";
};

const workflowTypeOf = (container) => {
  if (!container) return undefined;
  if (container.matches("[data-track-panel], .right-modal") || container.querySelector("[data-track-panel]")) return "panel";
  return "popup";
};

const AREA_SELECTOR = "[data-track-workflow]";

const readArea = (area) => {
  const name = (area.getAttribute("data-track-workflow") || "").trim();
  if (!name) return null;
  const type = area.getAttribute("data-track-workflow-type") || (area.matches("form") ? "form" : "section");
  return { workflow_name: name, workflow_type: type };
};

export const readWorkflow = (element) => {
  if (!element || typeof element.closest !== "function") return {};
  const area = element.closest(AREA_SELECTOR);
  const box = element.closest(PANEL_SELECTOR);
  if (area && (!box || box.contains(area))) {
    const found = readArea(area);
    if (found) return found;
  }
  const name = readPanel(element);
  if (!name) return {};
  return { workflow_name: name, workflow_type: workflowTypeOf(element.closest(PANEL_SELECTOR)) };
};

const GENERIC_TEXT = /^(select|choose|pick|search)?\s*(an?\s+)?(option|one|item|value)?\s*(\.{2,3}|…)?\s*$/i;
const meaningful = (text) => (text && !GENERIC_TEXT.test(text) ? text : "");

const nameSelect = (control) => {
  const named = nearestNamed(control, SELECT_NAME_LEVELS);
  if (named) return { name: readName(named), named: true };
  const container = control.closest(SELECT_CONTAINER) || control.parentElement;
  if (container) {
    if (container.id) return { name: container.id, named: true };
    const input = container.querySelector("input[name]");
    if (input && input.getAttribute("name")) return { name: input.getAttribute("name"), named: true };
    const labelled = container.querySelector("input[aria-label]");
    const fromAria = labelled ? meaningful(clean(labelled.getAttribute("aria-label"))) : "";
    if (fromAria) return { name: fromAria, named: false };
    const placeholder = container.querySelector("[class*='__placeholder']");
    const fromPlaceholder = placeholder ? meaningful(clean(placeholder.textContent)) : "";
    if (fromPlaceholder) return { name: fromPlaceholder, named: false };
    const group = container.closest(".form-group, label, td, .col, [class*='col-']");
    const label = group ? group.querySelector("label") : null;
    const fromLabel = label ? meaningful(clean(label.textContent)) : "";
    if (fromLabel) return { name: fromLabel, named: false };
  }
  return { name: "unnamed", named: false };
};

const handleSelect = (target) => {
  const option = target.closest(SELECT_OPTION);
  if (option) {
    const select = lastSelect || { name: "unnamed", named: false };
    track("dropdown", select.name, "choose", select.form ? undefined : clean(option.textContent), {
      unnamed: !select.named || undefined,
      component_id: select.componentId,
      form: select.form || undefined,
      ...select.workflow,
    });
    return true;
  }

  const control = target.closest(SELECT_CONTROL);
  if (!control) return false;

  const named = nameSelect(control);
  const select = { ...named, workflow: readWorkflow(control), form: Boolean(control.closest("form")), componentId: componentId(control, "dropdown", named.name, named.named) };
  lastSelect = select;
  const extra = { unnamed: !select.named || undefined, component_id: select.componentId, form: select.form || undefined, ...select.workflow };

  if (target.closest(SELECT_CLEAR)) track("dropdown", select.name, "clear", undefined, extra);
  else if (target.closest(SELECT_REMOVE)) track("dropdown", select.name, "remove", undefined, extra);
  else track("dropdown", select.name, "open", undefined, extra);
  return true;
};

const handleButton = (target) => {
  const clickable = target.closest(CLICKABLE_SELECTOR);
  const named = nearestNamed(clickable || target, BUTTON_NAME_LEVELS);
  const element = clickable || named;
  if (!element) return false;
  if (!clickable && named.querySelector(SELECT_CONTROL)) return true;
  if (element.hasAttribute("download") && !clean(element.textContent)) return true;

  const name = named ? readName(named) : "";
  const text = readLabel(clickable || element);
  const value = element.getAttribute("data-track-value") || undefined;
  lastClick = { name: name || text || "unnamed", at: Date.now() };

  if (!name && element.closest(IGNORE_SELECTOR)) return true;

  emit("button", name || "unnamed", "click", value, {
    text: text || undefined,
    unnamed: !name || undefined,
    component_id: componentId(element, "button", name, Boolean(name) && ownsName(named, element)),
    ...readWorkflow(element),
  });
  return true;
};

const readHeadingNear = (container) => {
  let current = container.parentElement;
  for (let level = 0; current && level < HEADING_LEVELS; level += 1) {
    const heading = current.querySelector(HEADING_SELECTOR);
    if (heading && clean(heading.textContent)) return clean(heading.textContent);
    current = current.parentElement;
  }
  return "";
};

export const nameChart = (container, titleSelector) => {
  const named = container.closest("[data-track-chart]") || nearestNamed(container, CHART_NAME_LEVELS);
  if (named) return { name: named.getAttribute("data-track-chart") || readName(named), named: true };
  const title = container.querySelector(titleSelector);
  const text = (title ? clean(title.textContent) : "") || readHeadingNear(container);
  if (text) return { name: text, named: false };
  const fallback = container.closest("[data-track-chart-fallback]");
  return { name: fallback ? fallback.getAttribute("data-track-chart-fallback") : "unnamed", named: false };
};

const reportChartClick = (container, titleSelector) => {
  const chart = nameChart(container, titleSelector);
  emit("chart", chart.name, "click", undefined, {
    unnamed: !chart.named || undefined,
    component_id: componentId(container, "chart", chart.name, chart.named),
    ...readWorkflow(container),
  });
  return true;
};

const readSeries = (target) => {
  const series = target.closest(".highcharts-series, .highcharts-markers");
  if (!series) return undefined;
  const label = series.getAttribute("aria-label");
  if (label) return clean(label.split(",")[0]);
  const match = String(series.getAttribute("class") || "").match(/highcharts-series-(\d+)/);
  if (!match) return undefined;
  const container = series.closest(".highcharts-container");
  const legend = container ? container.querySelector(`.highcharts-legend-item.highcharts-series-${match[1]}`) : null;
  const legendText = legend ? clean(legend.textContent) : "";
  return legendText || `series ${Number(match[1]) + 1}`;
};

const CHART_ACTIONS = [
  [".highcharts-legend-item", "legend", (element) => clean(element.textContent)],
  [".highcharts-reset-zoom", null, () => undefined],
  [".highcharts-menu-item", "export", (element) => clean(element.textContent)],
  [".highcharts-contextbutton", "menu", () => undefined],
  [".highcharts-range-selector-buttons .highcharts-button", "range", (element) => clean(element.textContent)],
  [".highcharts-point", "pointClick", (element) => readSeries(element)],
];

const handleChart = (target) => {
  const container = target.closest(".highcharts-container");
  if (!container) return false;
  for (let index = 0; index < CHART_ACTIONS.length; index += 1) {
    const [selector, action, readValue] = CHART_ACTIONS[index];
    const element = target.closest(selector);
    if (element && !action) return true;
    if (element) {
      const chart = nameChart(container, ".highcharts-title");
      emit("chart", chart.name, action, readValue(element), {
        unnamed: !chart.named || undefined,
        component_id: componentId(container, "chart", chart.name, chart.named),
        ...readWorkflow(container),
      });
      return true;
    }
  }
  return reportChartClick(container, ".highcharts-title");
};

const readLines = (label) => {
  const lines = label.querySelectorAll("tspan");
  if (!lines.length) return clean(label.textContent);
  return clean(
    Array.from(lines)
      .map((line) => line.textContent)
      .join(" ")
  );
};

const readSlice = (element) => {
  const group = element.closest("g.slice, g.trace, g.points, g.point") || element;
  const label = group.querySelector ? group.querySelector(".slicetext, text") : null;
  return label ? readLines(label) : undefined;
};

const PLOTLY_ACTIONS = [
  [".modebar-btn", "toolbar", (element) => clean(element.getAttribute("data-title") || element.getAttribute("aria-label"))],
  [".legend .traces", "legend", (element) => clean(element.textContent)],
  ["g.slice, .sunburstlayer path, .pielayer path, .treemaplayer path", "pointClick", readSlice],
  [".points path, .points .point, .barlayer path, .scatterlayer path", "pointClick", readSlice],
];

const handlePlotly = (target) => {
  const container = target.closest(".js-plotly-plot");
  if (!container) return false;
  for (let index = 0; index < PLOTLY_ACTIONS.length; index += 1) {
    const [selector, action, readValue] = PLOTLY_ACTIONS[index];
    const element = target.closest(selector);
    if (element) {
      const chart = nameChart(container, ".gtitle");
      emit("chart", chart.name, action, readValue(element), {
        unnamed: !chart.named || undefined,
        component_id: componentId(container, "chart", chart.name, chart.named),
        ...readWorkflow(container),
      });
      return true;
    }
  }
  return reportChartClick(container, ".gtitle");
};

const handleCanvas = (target) => {
  if (!target.matches || !target.matches("canvas")) return false;
  const chart = nameChart(target, ":scope > .none");
  emit("chart", chart.name, "click", undefined, {
    unnamed: !chart.named || undefined,
    component_id: componentId(target, "chart", chart.name, chart.named),
    ...readWorkflow(target),
  });
  return true;
};

const handlePageTab = (target) => {
  const tab = target.closest(PAGE_TAB_SELECTOR);
  if (!tab) return false;
  const named = nearestNamed(tab, BUTTON_NAME_LEVELS);
  const text = readLabel(tab);
  emit("pageTab", named ? readName(named) : text || "unnamed", "open", undefined, {
    text: text || undefined,
    unnamed: (!named && !text) || undefined,
    component_id: componentId(tab, "pageTab", named ? readName(named) : "", Boolean(named)),
    ...readWorkflow(tab),
  });
  return true;
};

const handleRow = (target) => {
  const row = target.closest("[data-track-row]");
  if (!row) return;
  const name = row.getAttribute("data-track-row");
  const cell = target.closest("[data-track-column]");
  emit("table", name, "rowClick", cell ? cell.getAttribute("data-track-column") : undefined, {
    unnamed: name === "unnamed" || undefined,
    component_id: componentId(row, "table", name, name !== "unnamed"),
    ...readWorkflow(row),
  });
};

let hover = { container: null, since: 0, reported: false };
let lastHoverCheck = 0;
let inspected = new WeakMap();

const handleMove = (event) => {
  const at = Date.now();
  if (at - lastHoverCheck < HOVER_CHECK_MS) return;
  lastHoverCheck = at;
  const target = event.target;
  if (!target || typeof target.closest !== "function") return;

  const container = target.closest(CHART_SELECTOR);
  if (container !== hover.container) {
    hover = { container, since: at, reported: false };
    return;
  }
  if (!container || hover.reported || at - hover.since < INSPECT_AFTER_MS) return;

  hover.reported = true;
  if (at - (inspected.get(container) || 0) < INSPECT_AGAIN_MS) return;
  inspected.set(container, at);
  const chart = nameChart(container, CHART_TITLE_SELECTOR);
  track("chart", chart.name, "inspect", undefined, {
    unnamed: !chart.named || undefined,
    component_id: componentId(container, "chart", chart.name, chart.named),
    ...readWorkflow(container),
  });
};

const handleClick = (event) => {
  const target = event.target;
  if (!target || typeof target.closest !== "function") return;
  if (handleSelect(target)) return;
  if (handleChart(target)) return;
  if (handlePlotly(target)) return;
  if (handleCanvas(target)) return;
  if (handlePageTab(target)) return;
  if (handleButton(target)) return;
  handleRow(target);
};

export const watchClicks = () => {
  if (typeof document === "undefined") return () => {};
  lastClick = null;
  lastSelect = null;
  lastEmitted = { key: "", at: 0 };
  hover = { container: null, since: 0, reported: false };
  lastHoverCheck = 0;
  inspected = new WeakMap();
  document.addEventListener("click", handleClick, true);
  document.addEventListener("mousemove", handleMove, { capture: true, passive: true });
  return () => {
    document.removeEventListener("click", handleClick, true);
    document.removeEventListener("mousemove", handleMove, { capture: true });
  };
};

export default watchClicks;
